import { API_BASE, ApiError, api } from "@/lib/apiClient";
import { peekCurrentUser, userCanSeePrices } from "@/lib/auth";

/* ─────────────────────────── Typen ─────────────────────────── */

export type MaStatus = "offen" | "in_bearbeitung" | "erledigt" | "abgelehnt";

/** Monteur-sichtbarer Bearbeitungsstand (vom Server berechnet). */
export type MaMonteurFortschritt =
  | "offen"
  | "in_bearbeitung"
  | "angefragt"
  | "bestellt"
  | "bereitgelegt"
  | "geliefert"
  | "abgelehnt";

export interface MaPositionPreview {
  menge: number | null;
  einheit: string | null;
  text: string;
  notes?: string | null;
}

export interface MaListItem {
  id: number;
  ma_nummer: string;
  status: MaStatus;
  kuerzel: string | null;
  title: string | null;
  notes: string | null;
  auftrag_id: number | null;
  auftrag_nummer: string | null;
  auftrag_ort?: string | null;
  auftrag_ort_zusatz?: string | null;
  auftrag_beschreibung?: string | null;
  auftrag_kunde?: string | null;
  auftrag_ansprechpartner?: string | null;
  liefertermin: string | null;
  liefertermin_fix: boolean;
  liefer_kw_von?: number | null;
  liefer_kw_bis?: number | null;
  liefer_kw_jahr?: number | null;
  buero_notizen?: string | null;
  bearbeitet_von: string | null;
  bearbeitet_von_name?: string | null;
  bearbeitet_at: string | null;
  fortschritt: string | null;
  monteur_fortschritt: MaMonteurFortschritt;
  /** True, wenn jede Position in mind. einer finalisierten TK steht. */
  all_positions_ordered?: boolean;
  bereitgelegt_at?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  position_count: number;
  attachment_count: number;
  position_preview?: MaPositionPreview[];
  has_anfrage: boolean;
  has_order: boolean;
  has_aga?: boolean;
  linked_aga?: MaLinkedAga | null;
  linked_ta_subjects?: string[];
  linked_bestellnummern?: string[];
  /** Verknüpfte Bestellungen inkl. stornierter (für TK-Badge-Farbe). */
  linked_tk?: MaLinkedTkBrief[];
}

export interface MaLinkedTkBrief {
  order_id: number;
  bestellnummer: string;
  cancelled_at?: string | null;
}

export interface MaPositionOrderedLink {
  order_id: number;
  bestellnummer?: string | null;
  supplier_name?: string | null;
  /** Zeitpunkt der Lieferung der verknüpften Bestellung. */
  geliefert_at?: string | null;
}

export interface MaPositionDraftLink {
  draft_id: number;
  supplier_name?: string | null;
}

export interface MaPosition {
  id: number;
  sort_index: number | null;
  menge: number | null;
  einheit: string | null;
  text: string;
  link_url: string | null;
  notes: string | null;
  /** Verknüpfung in den lokalen Hero-Artikelkatalog (`public.artikel.id`). */
  artikel_id: number | null;
  hero_nr: string | null;
  hero_unit: string | null;
  ean: string | null;
  /** Position stammt aus KI-Bildanalyse und sollte geprüft werden. */
  from_ai?: boolean;
  created_at?: string | null;
  /** Zeitpunkt, zu dem diese Position im Lager bereitgelegt wurde. */
  bereitgelegt_at?: string | null;
  /** Einzelposition: storniert oder abgelehnt (Büro). */
  position_status?: "storniert" | "abgelehnt" | null;
  position_status_at?: string | null;
  position_status_von?: string | null;
  position_status_grund?: string | null;
  /** Finalisierte, nicht stornierte TK-Zeilen mit dieser MA-Position. */
  ordered_links?: MaPositionOrderedLink[];
  /** Offene TK-Entwürfe derselben MA, die diese Position enthalten. */
  draft_links?: MaPositionDraftLink[];
}

export type MaPositionDisposition = "storniert" | "abgelehnt";

export interface DisposeMaPositionsIn {
  status: MaPositionDisposition;
  grund: string;
  position_ids: number[];
}

export interface MarkMaBereitgelegtIn {
  /** Optional: nur diese Positionen; fehlt/undefined = alle. */
  position_ids?: number[];
}

export interface MaAttachment {
  id: number;
  position_id: number | null;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_by: string;
  uploaded_at: string;
}

export interface MaLinkedAnfrage {
  anfrage_id: number;
  ta_subject: string | null;
  status: string | null;
  anfrage_group_id: number | null;
  supplier_name: string | null;
  created_at: string;
}

export interface MaLinkedOrder {
  order_id: number;
  subject: string | null;
  bestellnummer?: string | null;
  supplier_name: string | null;
  created_at: string;
  cancelled_at?: string | null;
  geliefert_at?: string | null;
}

export interface MaLinkedOrderDraft {
  draft_id: number;
  supplier_name: string | null;
  created_at: string;
  created_by: string | null;
}

export interface MaLinkedAga {
  aga_id: number;
  aga_nummer: string;
  status: string | null;
}

export interface MaCreateLinkedAgaInput {
  contact_mode: "project" | "new";
  ansprechpartner_id?: number | null;
  contact_name?: string | null;
  contact_company?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  contact_note?: string | null;
}

export interface MaDetail {
  id: number;
  ma_nummer: string;
  status: MaStatus;
  kuerzel: string | null;
  title: string | null;
  notes: string | null;
  auftrag_id: number | null;
  auftrag_nummer: string | null;
  auftrag_ort?: string | null;
  auftrag_ort_zusatz?: string | null;
  auftrag_beschreibung?: string | null;
  auftrag_kunde?: string | null;
  auftrag_ansprechpartner?: string | null;
  liefertermin: string | null;
  liefertermin_fix: boolean;
  liefer_kw_von?: number | null;
  liefer_kw_bis?: number | null;
  liefer_kw_jahr?: number | null;
  buero_notizen?: string | null;
  bearbeitet_von: string | null;
  bearbeitet_von_name?: string | null;
  bearbeitet_at: string | null;
  fortschritt: string | null;
  monteur_fortschritt: MaMonteurFortschritt;
  /** True, wenn jede Position in mind. einer finalisierten TK steht. */
  all_positions_ordered?: boolean;
  bereitgelegt_at?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  positionen: MaPosition[];
  attachments: MaAttachment[];
  linked_anfragen: MaLinkedAnfrage[];
  linked_orders: MaLinkedOrder[];
  linked_order_drafts?: MaLinkedOrderDraft[];
  linked_aga?: MaLinkedAga | null;
  takeover_from?: string | null;
  takeover_from_name?: string | null;
  storno_grund?: string | null;
  storniert_von?: string | null;
  storniert_at?: string | null;
}

export interface MaPositionInput {
  sort_index?: number | null;
  menge: number | null;
  einheit: string | null;
  text: string;
  link_url: string | null;
  notes: string | null;
  /** Verknüpfung mit Hero-Artikel (lokaler `artikel.id`); `null`, wenn frei. */
  artikel_id?: number | null;
  hero_nr?: string | null;
  hero_unit?: string | null;
  ean?: string | null;
  from_ai?: boolean;
}

export interface MaCreateInput {
  title: string | null;
  notes: string | null;
  auftrag_id: number | null;
  positionen: MaPositionInput[];
  kuerzel?: string | null;
  liefertermin?: string | null;
  liefertermin_fix?: boolean;
  liefer_kw_von?: number | null;
  liefer_kw_bis?: number | null;
  liefer_kw_jahr?: number | null;
}

export interface MaUpdateInput {
  title?: string | null;
  notes?: string | null;
  auftrag_id?: number | null;
  liefertermin?: string | null;
  liefertermin_fix?: boolean;
  liefer_kw_von?: number | null;
  liefer_kw_bis?: number | null;
  liefer_kw_jahr?: number | null;
  buero_notizen?: string | null;
  fortschritt?: "offen" | "in_bearbeitung" | "angefragt" | "bestellt" | "bereitgelegt" | "geliefert";
}

/* ─────────────────────────── Liste / Detail ─────────────────────────── */

export interface ListMaParams {
  status?: MaStatus | "";
  q?: string;
  mineOnly?: boolean;
  claimedOnly?: boolean;
  /** Nur Anforderungen dieses Auftrags (Projektübersicht). */
  auftragId?: number | null;
}

export async function listMaterialAnforderungen(
  params: ListMaParams = {},
): Promise<MaListItem[]> {
  const usp = new URLSearchParams();
  if (params.status) usp.set("status", params.status);
  if (params.q) usp.set("q", params.q);
  if (params.mineOnly) usp.set("mine_only", "true");
  if (params.claimedOnly) usp.set("claimed_only", "true");
  if (params.auftragId != null) usp.set("auftrag_id", String(params.auftragId));
  const qs = usp.toString();
  const path = qs ? `/material-anforderungen?${qs}` : "/material-anforderungen";
  return api.get<MaListItem[]>(path);
}

export async function getMaterialAnforderung(id: number): Promise<MaDetail> {
  return api.get<MaDetail>(`/material-anforderungen/${id}`);
}

export async function createMaterialAnforderung(
  payload: MaCreateInput,
): Promise<MaDetail> {
  return api.post<MaDetail>("/material-anforderungen", payload);
}

export async function createLinkedAgaFromMa(
  maId: number,
  payload: MaCreateLinkedAgaInput,
): Promise<{ id: number; aga_nummer: string }> {
  return api.post<{ id: number; aga_nummer: string }>(
    `/material-anforderungen/${maId}/create-linked-aga`,
    payload,
  );
}

// Der Server unterstützt PATCH – wir nutzen einen kleinen Wrapper, weil
// `api` selbst nur GET/POST/PUT/DELETE hat. Statt das ApiClient zu erweitern,
// verwenden wir hier `fetch` über `apiClient`-Konventionen.
export async function patchMaterialAnforderung(
  id: number,
  payload: MaUpdateInput,
): Promise<MaDetail> {
  const res = await fetch(`${API_BASE}/material-anforderungen/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return (await res.json()) as MaDetail;
}

export async function deleteMaterialAnforderung(id: number): Promise<void> {
  await api.del(`/material-anforderungen/${id}`);
}

export async function setMaterialAnforderungStatus(
  id: number,
  status: MaStatus,
): Promise<MaDetail> {
  return api.post<MaDetail>(`/material-anforderungen/${id}/status`, { status });
}

export async function claimMaterialAnforderung(id: number): Promise<MaDetail> {
  return api.post<MaDetail>(`/material-anforderungen/${id}/claim`, {});
}

export interface MaBueroAssignee {
  user_login: string;
  display_name: string;
}

export async function listMaBueroAssignees(): Promise<MaBueroAssignee[]> {
  const data = await api.get<MaBueroAssignee[]>("/material-anforderungen/buero-assignees");
  return Array.isArray(data) ? data : [];
}

export async function releaseMaterialAnforderung(
  id: number,
  grund: string,
): Promise<MaDetail> {
  return api.post<MaDetail>(`/material-anforderungen/${id}/release`, { grund });
}

export async function assignMaterialAnforderung(
  id: number,
  payload: { assignee_login: string; grund: string },
): Promise<MaDetail> {
  return api.post<MaDetail>(`/material-anforderungen/${id}/assign`, payload);
}

export async function stornierenMaterialAnforderung(
  id: number,
  grund: string,
): Promise<MaDetail> {
  return api.post<MaDetail>(`/material-anforderungen/${id}/stornieren`, { grund });
}

export async function markMaBereitgelegt(
  id: number,
  data: MarkMaBereitgelegtIn = {},
): Promise<{
  ok: boolean;
  bereitgelegt_at?: string | null;
  ma: MaDetail;
  already_marked?: boolean;
  partial?: boolean;
  marked_position_ids?: number[];
}> {
  return api.post(`/material-anforderungen/${id}/mark-bereitgelegt`, data);
}

export async function unmarkMaBereitgelegt(
  id: number,
  data: MarkMaBereitgelegtIn = {},
): Promise<{ ok: boolean; ma: MaDetail; already_unmarked?: boolean }> {
  return api.post(`/material-anforderungen/${id}/unmark-bereitgelegt`, data);
}

export async function disposeMaPositions(
  id: number,
  data: DisposeMaPositionsIn,
): Promise<{
  ok: boolean;
  status: MaPositionDisposition;
  position_ids: number[];
  ma: MaDetail;
}> {
  return api.post(`/material-anforderungen/${id}/positions/disposition`, data);
}

export async function clearMaPositionDisposition(
  id: number,
  data: { position_ids: number[] },
): Promise<{ ok: boolean; position_ids: number[]; ma: MaDetail }> {
  return api.post(`/material-anforderungen/${id}/positions/disposition/clear`, data);
}

export type MaLagerPdfOnly = "bereitgelegt" | "all";

export function materialAnforderungPdfUrl(
  id: number,
  only: MaLagerPdfOnly = "bereitgelegt",
): string {
  const qs = new URLSearchParams({ only });
  return `${API_BASE}/material-anforderungen/${encodeURIComponent(String(id))}/pdf?${qs}`;
}

/** Lädt die Lager-Zusammenfassung und öffnet sie in einem neuen Tab zum Drucken. */
export async function openMaterialAnforderungLagerPdf(
  id: number,
  only: MaLagerPdfOnly = "bereitgelegt",
): Promise<void> {
  const res = await fetch(materialAnforderungPdfUrl(id, only), {
    credentials: "include",
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = (await res.json()) as { detail?: unknown };
      if (typeof parsed?.detail === "string" && parsed.detail.trim()) {
        message = parsed.detail.trim();
      }
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, "_blank", "noopener");
  if (!opened) {
    // Popup blockiert → Download-Fallback
    const a = document.createElement("a");
    a.href = url;
    a.download = `MA-${id}_Lager.pdf`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function fetchMaterialOpenCount(): Promise<number> {
  const data = await api.get<{ count?: number }>("/material-anforderungen/open-count");
  return Number(data?.count || 0);
}

export interface MaterialBadgeCounts {
  open: number;
  open_urgent_fix: number;
  in_bearbeitung: number;
}

export async function fetchMaterialBadgeCounts(): Promise<MaterialBadgeCounts> {
  const data = await api.get<{
    open?: number;
    open_urgent_fix?: number;
    in_bearbeitung?: number;
  }>("/material-anforderungen/badge-counts");
  return {
    open: Number(data?.open || 0),
    open_urgent_fix: Number(data?.open_urgent_fix || 0),
    in_bearbeitung: Number(data?.in_bearbeitung || 0),
  };
}

export async function fetchMaterialDeliveredUnseenCount(): Promise<number> {
  const data = await api.get<{ count?: number }>(
    "/material-anforderungen/delivered-unseen-count",
  );
  return Number(data?.count || 0);
}

export interface ReadyMaterialItem {
  id: number;
  ma_nummer?: string | null;
  title?: string | null;
  auftrag_nummer?: string | null;
  auftrag_beschreibung?: string | null;
  auftrag_ort?: string | null;
  monteur_fortschritt?: MaMonteurFortschritt | string | null;
  position_count?: number | null;
  position_preview?: MaPositionPreview[];
}

/** Bereitgelegte oder gelieferte eigene MAs, die der Monteur noch nicht gesehen hat. */
export async function fetchDeliveredUnseenMaterial(): Promise<ReadyMaterialItem[]> {
  const data = await api.get<ReadyMaterialItem[]>("/material-anforderungen/delivered-unseen");
  return Array.isArray(data) ? data : [];
}

/** Markiert eine gelieferte/bereitgelegte MA als angesehen (grünes Nav-Badge ausblenden). */
export async function markMaterialAnforderungSeen(
  id: number,
): Promise<{ ok: boolean; seen?: boolean }> {
  return api.post(`/material-anforderungen/${id}/mark-seen`, {});
}

/* ─────────────────────────── Positionen ─────────────────────────── */

export async function addMaPosition(
  maId: number,
  payload: MaPositionInput,
): Promise<{ id: number; ma: MaDetail }> {
  return api.post<{ id: number; ma: MaDetail }>(
    `/material-anforderungen/${maId}/positions`,
    payload,
  );
}

export async function patchMaPosition(
  maId: number,
  pid: number,
  payload: Partial<MaPositionInput>,
): Promise<MaDetail> {
  const res = await fetch(
    `${API_BASE}/material-anforderungen/${maId}/positions/${pid}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return (await res.json()) as MaDetail;
}

export async function deleteMaPosition(
  maId: number,
  pid: number,
): Promise<void> {
  await api.del(`/material-anforderungen/${maId}/positions/${pid}`);
}

/* ─────────────────────────── Anhänge ─────────────────────────── */

export interface AddAttachmentResult {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  position_id: number | null;
  uploaded_at: string;
}

export async function addMaAttachment(
  maId: number,
  file: File,
  positionId: number | null,
): Promise<AddAttachmentResult> {
  const fd = new FormData();
  fd.append("file", file);
  if (positionId != null) fd.append("position_id", String(positionId));
  return api.postForm<AddAttachmentResult>(
    `/material-anforderungen/${maId}/attachments`,
    fd,
  );
}

export async function deleteMaAttachment(
  maId: number,
  attachmentId: number,
): Promise<void> {
  await api.del(`/material-anforderungen/${maId}/attachments/${attachmentId}`);
}

export function getMaAttachmentUrl(maId: number, attachmentId: number): string {
  const base = API_BASE || "";
  return `${base}/material-anforderungen/${maId}/attachments/${attachmentId}`;
}

/** Verkleinerte Anzeigequalität (schnell laden, weiterhin scharf). */
export function getMaAttachmentThumbUrl(maId: number, attachmentId: number): string {
  return `${getMaAttachmentUrl(maId, attachmentId)}/thumb`;
}

export interface MaMobileUploadQrResponse {
  url?: string;
  qr_data_uri?: string;
  expires_at?: string | null;
}

export async function getMaMobileUploadQr(
  maId: number,
): Promise<MaMobileUploadQrResponse> {
  return api.get<MaMobileUploadQrResponse>(
    `/material-anforderungen/${maId}/mobile-upload-qr`,
  );
}

export type MaCreateQrPaperSize = "A4" | "A3";

export interface MaCreateQrAuftrag {
  id: number;
  auftragsnummer: string;
  jahr?: number | null;
  kunde?: string | null;
  ort?: string | null;
  ort_zusatz?: string | null;
  ort_line?: string | null;
  beschreibung?: string | null;
}

export interface MaCreateQrResponse {
  url: string;
  qr_data_uri: string;
  auftrag: MaCreateQrAuftrag;
}

export interface MaCreateQrPrintUrlResponse {
  url: string;
  expires_at?: number;
  paper_size?: MaCreateQrPaperSize;
}

export function getMaterialAnforderungCreateQr(
  auftragId: number,
): Promise<MaCreateQrResponse> {
  return api.get<MaCreateQrResponse>(
    `/auftraege/${encodeURIComponent(String(auftragId))}/material-anforderung-qr`,
    { cache: "no-store" },
  );
}

export function getMaterialAnforderungCreateQrPrintUrl(
  auftragId: number,
  size: MaCreateQrPaperSize,
): Promise<MaCreateQrPrintUrlResponse> {
  const params = new URLSearchParams({ size });
  return api.get<MaCreateQrPrintUrlResponse>(
    `/auftraege/${encodeURIComponent(String(auftragId))}/material-anforderung-qr/pdf/print-url?${params}`,
    { cache: "no-store" },
  );
}

/* ─────────────────────────── Convert / Notify ─────────────────────────── */

export interface ConvertResult {
  anfrage_id?: number;
  order_draft_id?: number;
  order_id?: number;
  ma_id: number;
  ma_nummer: string;
}

export async function convertMaToAnfrage(
  maId: number,
): Promise<ConvertResult> {
  return api.post<ConvertResult>(
    `/material-anforderungen/${maId}/convert-to-anfrage`,
    {},
  );
}

export interface ConvertMaToOrderIn {
  supplier_id: string | number;
  supplier_name?: string | null;
  supplier_contact_first_name?: string | null;
  supplier_contact_last_name?: string | null;
  supplier_contact_anrede?: string | null;
  supplier_contact_name?: string | null;
  supplier_email?: string | null;
  /** Nur diese MA-Positionen übernehmen; fehlt = alle. */
  position_ids?: number[];
}

export async function convertMaToOrder(
  maId: number,
  data: ConvertMaToOrderIn,
): Promise<ConvertResult> {
  return api.post<ConvertResult>(
    `/material-anforderungen/${maId}/convert-to-order`,
    data,
  );
}

export interface LinkMaOrderResult {
  ma_id: number;
  order_id: number;
  previous_ma_id: number | null;
  attached_position_ids?: number[];
  created_line_ids?: number[];
}

export interface UnlinkMaOrderResult {
  ma_id: number;
  order_id: number;
  unlinked: boolean;
}

export async function linkMaOrder(
  maId: number,
  orderId: number,
  positionIds?: number[],
): Promise<LinkMaOrderResult> {
  return api.post<LinkMaOrderResult>(
    `/material-anforderungen/${maId}/link-order`,
    {
      order_id: orderId,
      ...(positionIds != null ? { position_ids: positionIds } : {}),
    },
  );
}

export async function unlinkMaOrder(
  maId: number,
  orderId: number,
): Promise<UnlinkMaOrderResult> {
  return api.del<UnlinkMaOrderResult>(
    `/material-anforderungen/${maId}/link-order/${orderId}`,
  );
}

export interface NotifyResult {
  ok: boolean;
  via?: string;
  reason?: string;
}

export async function notifyBueroForMa(maId: number): Promise<NotifyResult> {
  return api.post<NotifyResult>(
    `/material-anforderungen/${maId}/notify-buero`,
    {},
  );
}

/* ─────────────────────────── KI-Bildanalyse ─────────────────────────── */

export type AiKind =
  | "skizze"
  | "handschrift"
  | "ausgedruckt"
  | "kein_material"
  | "foto";

export type AiCategory =
  | "blech"
  | "schraube"
  | "mutter"
  | "profil"
  | "rohr"
  | "dichtung"
  | "kabel"
  | "elektro"
  | "werkzeug"
  | "sonstiges";

export interface AiMissingField {
  field: string;
  label: string;
  question: string;
  examples: string[];
}

export interface AiSuggestedPosition {
  menge: number | null;
  einheit: string | null;
  text: string;
  category: AiCategory;
  notes: string | null;
  missing: AiMissingField[];
}

export interface AiAuftragHint {
  /** Original-Text wie auf dem Bild (z. B. "1-1#25"). */
  value: string;
  /** Sicherheit, mit der die Erkennung erfolgte (0..1). */
  confidence: number;
  /** Kurzer Hinweis, wo die Nummer erkannt wurde (optional). */
  context: string | null;
}

export interface AiAnalysisResult {
  kind: AiKind;
  confidence: number;
  global_summary: string;
  /** Optional: erkannte Auftragsnummer im Format <ordnernummer>#<lauf>. */
  auftrag_hint: AiAuftragHint | null;
  positions: AiSuggestedPosition[];
  warnings: string[];
  model: string;
}

/**
 * Sendet ein Bild an den Backend-Endpoint, der per OpenAI-Vision die
 * Materialliste / Skizze auswertet.  HEIC/HEIF wird vom Server abgelehnt –
 * der Browser konvertiert üblicherweise schon vor dem Upload.
 */
export async function analyzeImageWithAi(file: File): Promise<AiAnalysisResult> {
  const fd = new FormData();
  fd.append("file", file);
  return api.postForm<AiAnalysisResult>(
    "/material-anforderungen/ai/analyze-image",
    fd,
  );
}

/* ─────────────────────────── Auftragssuche ─────────────────────────── */

/**
 * Schlanke Suche nach Aufträgen, um im MA-Dialog ein Autocomplete-Feld zu
 * füttern. Wir nutzen denselben `/auftraege?q=…`-Endpoint wie der
 * Projekt-Listenfilter.
 */
/** Statuse, auf die keine Stunden mehr gebucht werden dürfen (wie Backend). */
export const HOUR_BOOKING_BLOCKED_STATUSES = new Set([
  "fertiggestellt",
  "abgerechnet",
  "bezahlt",
]);

export function isHourBookingBlockedStatus(status: string | null | undefined): boolean {
  const key = String(status || "").trim().toLowerCase();
  return HOUR_BOOKING_BLOCKED_STATUSES.has(key);
}

export function hasLateHourPermission(row: {
  late_hour_permission?: boolean | null;
}): boolean {
  return row.late_hour_permission === true;
}

export function isBookableForHours(row: {
  project_completed?: boolean | null;
  workflow_status?: string | null;
  late_hour_permission?: boolean | null;
}): boolean {
  if (String(row.workflow_status || "").trim().toLowerCase() === "abgelehnt") return false;
  if (hasLateHourPermission(row)) return true;
  if (row.project_completed === true) return false;
  return !isHourBookingBlockedStatus(row.workflow_status);
}

export interface AuftragLookup {
  id: number;
  auftragsnummer: string;
  jahr?: number | null;
  beschreibung: string | null;
  ort: string | null;
  ort_zusatz: string | null;
  kunde: string | null;
  workflow_status?: string | null;
  project_completed?: boolean | null;
  late_hour_permission?: boolean | null;
}

interface RawAuftragRow {
  id: number;
  auftragsnummer?: string | null;
  jahr?: number | null;
  beschreibung?: string | null;
  ort?: string | null;
  ort_zusatz?: string | null;
  kunde?: string | null;
  workflow_status?: string | null;
  project_completed?: boolean | null;
  late_hour_permission?: boolean | null;
}

function mapAuftragRow(row: RawAuftragRow): AuftragLookup {
  const jahrRaw = row.jahr;
  const jahr =
    typeof jahrRaw === "number" && Number.isFinite(jahrRaw) ? jahrRaw : null;
  return {
    id: Number(row.id),
    auftragsnummer: String(row.auftragsnummer ?? ""),
    jahr,
    beschreibung: row.beschreibung ?? null,
    ort: row.ort ?? null,
    ort_zusatz: row.ort_zusatz ?? null,
    kunde: row.kunde ?? null,
    workflow_status: row.workflow_status ?? null,
    project_completed:
      typeof row.project_completed === "boolean" ? row.project_completed : null,
    late_hour_permission: row.late_hour_permission === true,
  };
}

/**
 * Aufträge, mit denen der angemeldete Benutzer zuletzt am intensivsten
 * interagiert hat (Detail-Aufrufe, Stundennachweise, Notizen, …).  Für das
 * "Klick ins leere Feld → meine letzten Aufträge"-Verhalten der klassischen
 * App.  Reichweite (`days`) und Limit sind serverseitig begrenzt.
 */
export async function getRecentAuftraege(
  options: {
    limit?: number;
    days?: number;
    includeCompleted?: boolean;
    forUserLogin?: string | null;
  } = {},
): Promise<AuftragLookup[]> {
  const params = new URLSearchParams();
  if (options.limit) params.set("limit", String(options.limit));
  if (options.days) params.set("days", String(options.days));
  if (options.includeCompleted === false) params.set("include_completed", "false");
  if (options.forUserLogin) params.set("late_hour_for_login", options.forUserLogin);
  const qs = params.toString();
  const list = await api
    .get<RawAuftragRow[]>(qs ? `/me/recent-auftraege?${qs}` : "/me/recent-auftraege")
    .catch(() => [] as RawAuftragRow[]);
  return (list || [])
    .filter((row) => row && row.id != null)
    .map(mapAuftragRow);
}

export async function searchAuftraege(
  query: string,
  options: { forUserLogin?: string | null } = {},
): Promise<AuftragLookup[]> {
  const q = (query || "").trim();
  if (!q) return [];
  const params = new URLSearchParams();
  params.set("q", q);
  params.set("limit", "20");
  params.set("offset", "0");
  params.set("include_timesheets", "false");
  params.set("include_timesheet_numbers", "false");
  params.set("include_tags", "false");
  params.set("include_hero_custom_fields", "false");
  if (options.forUserLogin) params.set("late_hour_for_login", options.forUserLogin);
  const list = await api
    .get<RawAuftragRow[]>(`/auftraege?${params.toString()}`)
    .catch(() => [] as RawAuftragRow[]);
  return (list || [])
    .filter((row) => row && row.id != null)
    .map(mapAuftragRow);
}

/**
 * Sucht einen Auftrag anhand seiner exakten Auftragsnummer (z. B. "1-1#25").
 * Liefert genau einen Treffer zurück, falls die Nummer eindeutig ist – sonst
 * `null`. Vergleich ist case-insensitive und ignoriert Whitespace.
 */
export async function findAuftragByNumber(
  number: string,
): Promise<AuftragLookup | null> {
  const wanted = (number || "").trim().toUpperCase();
  if (!wanted) return null;
  // Backend-Suche q= sucht über Volltextfelder; eine exakte
  // Auftragsnummer wie "1-1#25" wird darin sicher gefunden.
  const list = await searchAuftraege(wanted);
  const exact = list.filter(
    (row) => (row.auftragsnummer || "").trim().toUpperCase() === wanted,
  );
  if (exact.length === 1) return exact[0] ?? null;
  if (exact.length === 0) return null;
  // Mehrere Treffer wären inkonsistent – wir geben null zurück, damit der
  // Nutzer manuell entscheidet.
  return null;
}

/* ─────────────────────────── Hero-Artikel-Suche ─────────────────────────── */

/**
 * Ein Treffer der Hero-Artikel-Suche, kompatibel zum klassischen
 * `materialSuggestions`-Endpunkt der Stundennachweise. Wir holen genau die
 * Felder, die wir später an einer Position cachen wollen (Hero-Nummer,
 * Einheit, EAN, Artikel-ID).
 */
export interface HeroArticleSuggestion {
  /** Anzeigetext, z. B. "Dichtungsband 30×3 mm". */
  label: string;
  /** Wert-String (i. d. R. die Hero-Nr oder das Label). */
  value: string;
  /** Lokale Artikel-ID (`public.artikel.id`); ggf. `null` für reine Freitext-Treffer. */
  artikel_id: number | null;
  hero_nr: string | null;
  hero_base_name?: string | null;
  hero_base_description?: string | null;
  hero_unit: string | null;
  einheit: string | null;
  ean: string | null;
  /** Bild aus dem Lieferantenkatalog (`artikel.hero_base_image_src`). */
  image_src?: string | null;
  /** VK netto aus dem lokalen Hero-Snapshot (Standard-Verkaufspreis oder Listenpreis). */
  verkaufspreis_netto?: number | null;
  hero_vat_percent?: number | null;
}

/** Bezeichnung für Artikel/Bestellpositionen: Name vor Beschreibung, nie die Artikelnummer. */
export function heroArticleOrderText(article: {
  label?: string | null;
  text?: string | null;
  hero_base_description?: string | null;
  hero_base_name?: string | null;
}): string {
  const name = (article.hero_base_name || "").trim();
  const description = (article.hero_base_description || "").trim();
  const fallback = (article.label || article.text || "").trim();
  // Beschreibung oft HTML/Preishistorie – nur als Fallback, wenn kein Name existiert.
  if (name) return name;
  if (fallback && fallback !== description) return fallback;
  return description || fallback || "Artikel";
}

interface RawMaterialSuggestion {
  label?: string | null;
  value?: string | null;
  artikel_id?: number | null;
  hero_nr?: string | null;
  hero_base_name?: string | null;
  hero_base_description?: string | null;
  hero_unit?: string | null;
  einheit?: string | null;
  ean?: string | null;
  ean_code?: string | null;
  barcode?: string | null;
  unit?: string | null;
  verkaufspreis_netto?: number | null;
  hero_default_sales_net_price?: number | null;
  hero_list_price?: number | null;
  hero_vat_percent?: number | null;
  image_src?: string | null;
  hero_base_image_src?: string | null;
}

/**
 * Sucht Hero-Artikel über den bestehenden `/materials/suggestions`-Endpoint.
 * Verwendet dieselben Query-Flags wie die Stundennachweise (`tokens_all=1`,
 * `normalize=1`, `typo=1`), damit Tippfehler verziehen werden.
 */
function toHeroArticleSuggestion(
  row: RawMaterialSuggestion,
  includePrices: boolean,
): HeroArticleSuggestion | null {
  const heroBaseDescription = (row.hero_base_description || "").trim() || null;
  const heroBaseName = (row.hero_base_name || "").trim() || null;
  const label =
    heroBaseName ||
    (row.label || "").trim() ||
    heroBaseDescription ||
    String(row.value ?? "");
  if (!label) return null;
  return {
    label,
    value: String(row.value ?? label),
    artikel_id: row.artikel_id ?? null,
    hero_nr: row.hero_nr ?? null,
    hero_base_name: heroBaseName,
    hero_base_description: heroBaseDescription,
    hero_unit: row.hero_unit ?? null,
    einheit: row.einheit ?? row.hero_unit ?? row.unit ?? null,
    ean: row.ean ?? row.ean_code ?? row.barcode ?? null,
    image_src: (row.image_src || row.hero_base_image_src || "").trim() || null,
    verkaufspreis_netto: includePrices
      ? (row.verkaufspreis_netto ??
        row.hero_default_sales_net_price ??
        row.hero_list_price ??
        null)
      : null,
    hero_vat_percent: includePrices ? (row.hero_vat_percent ?? null) : null,
  };
}

export async function searchHeroArticles(
  query: string,
  options?: { limit?: number },
): Promise<HeroArticleSuggestion[]> {
  const q = (query || "").trim();
  if (!q) return [];
  const params = new URLSearchParams();
  params.set("q", q);
  params.set("tokens_all", "1");
  params.set("normalize", "1");
  params.set("typo", "1");
  params.set("limit", String(options?.limit ?? 20));
  const list = await api
    .get<RawMaterialSuggestion[]>(`/materials/suggestions?${params.toString()}`)
    .catch(() => [] as RawMaterialSuggestion[]);
  const showPrices = userCanSeePrices(peekCurrentUser());
  return (list || [])
    .map((row) => toHeroArticleSuggestion(row, showPrices))
    .filter((row): row is HeroArticleSuggestion => Boolean(row));
}

/** Einzelner Artikel für Etiketten-QR (auch für Monteure, ohne /artikel-ACL). */
export async function getHeroArticleById(artikelId: number): Promise<HeroArticleSuggestion> {
  const row = await api.get<RawMaterialSuggestion>(`/materials/artikel/${artikelId}`);
  const mapped = toHeroArticleSuggestion(row, userCanSeePrices(peekCurrentUser()));
  if (!mapped) {
    throw new ApiError(404, "Artikel nicht gefunden.");
  }
  return mapped;
}
